import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, MapPin } from 'lucide-react';
import { Link } from 'react-router-dom';
import { WEBSITE_LOCAL_PAGES, localPreposition } from '../../data/websiteLocalPages';
import type { WebsiteLocalPage } from '../../data/websiteLocalPages';
import { PRIMARY_CTA_CLASS, SECONDARY_CTA_CLASS } from './ctaStyles';
import { OfferFormCta, OfferWhatsAppCta } from './OfferCtaButtons';
import { HERO_BADGES, MONTHLY_PRICE, SETUP_PRICE, VAT_MENTION, formatEuro } from './offerData';

interface PageProps {
  page: WebsiteLocalPage;
}

export const WebsiteLocalHero: React.FC<PageProps> = ({ page }) => (
  <section className="relative overflow-hidden bg-[#2F3136] py-16 md:py-20">
    <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.55, ease: 'easeOut' }}
      >
        <p className="inline-flex items-center gap-2 rounded-full border border-[#5865F2]/40 bg-[#5865F2]/15 px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-[#B9C4FF] mb-4">
          <MapPin className="h-3.5 w-3.5" aria-hidden="true" /> {page.shortName}
        </p>
        <h1 className="text-balance text-3xl md:text-5xl font-bold text-white mb-4">{page.h1}</h1>
        <p className="text-xl md:text-2xl font-semibold text-white mb-3">
          <span className="text-[#5865F2]">{formatEuro(SETUP_PRICE)}</span> à la mise en ligne, puis{' '}
          <span className="text-[#5865F2]">{formatEuro(MONTHLY_PRICE)}/mois</span>
        </p>
        <p className="text-gray-300 text-sm mb-6">Sans acompte · {VAT_MENTION}</p>
        <div className="flex flex-wrap items-center justify-center gap-3 mb-8">
          {HERO_BADGES.map(({ label, icon: Icon }) => (
            <span key={label} className="flex items-center gap-1.5 bg-white/10 border border-white/20 rounded-full px-4 py-1.5 text-sm text-gray-100">
              <Icon className="h-4 w-4 text-[#5865F2]" aria-hidden="true" /> {label}
            </span>
          ))}
        </div>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-4">
          <OfferWhatsAppCta location={`local_${page.slug}_hero`} className={`${PRIMARY_CTA_CLASS} w-full sm:w-auto`} />
          <OfferFormCta location={`local_${page.slug}_hero`} className={`${SECONDARY_CTA_CLASS} w-full sm:w-auto`} />
        </div>
      </motion.div>
    </div>
  </section>
);

export const WebsiteLocalIntro: React.FC<PageProps> = ({ page }) => (
  <section className="py-14 bg-[#36393F]">
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
      <h2 className="text-balance text-2xl md:text-3xl font-bold text-white mb-5">
        Un site internet pour votre activité, {localPreposition(page)}
      </h2>
      <div className="space-y-4 text-gray-300 leading-relaxed">
        {page.intro.map((paragraph) => (
          <p key={paragraph}>{paragraph}</p>
        ))}
      </div>
    </div>
  </section>
);

export const WebsiteLocalNearby: React.FC<PageProps> = ({ page }) => {
  const otherPages = WEBSITE_LOCAL_PAGES.filter((entry) => entry.slug !== page.slug);
  return (
    <section className="py-14 bg-[#2F3136]">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-balance text-2xl font-bold text-white mb-4 text-center">Nous intervenons aussi à</h2>
        <ul className="flex flex-wrap justify-center gap-2 mb-8">
          {page.nearbyTowns.map((town) => (
            <li key={town} className="rounded-full border border-white/15 bg-white/5 px-3 py-1 text-sm text-gray-200">
              {town}
            </li>
          ))}
        </ul>
        <ul className="flex flex-col items-center gap-2">
          {otherPages.map((entry) => (
            <li key={entry.path}>
              <Link to={entry.path} className="inline-flex items-center gap-1.5 text-sm text-[#9BA8FF] hover:text-white transition-colors">
                <ArrowRight className="h-4 w-4" aria-hidden="true" /> Création de site internet {localPreposition(entry)}
              </Link>
            </li>
          ))}
          <li>
            <Link to="/creation-site-web" className="inline-flex items-center gap-1.5 text-sm text-[#9BA8FF] hover:text-white transition-colors">
              <ArrowRight className="h-4 w-4" aria-hidden="true" /> Découvrir toute l'offre de création de site internet
            </Link>
          </li>
        </ul>
      </div>
    </section>
  );
};
